import { useEffect, useRef, useState } from "react"

/**
 * 滚动隐藏 Hook
 * @description 监听页面滚动，当向下滚动超过阈值时隐藏，向上滚动时显示
 * @param isVisible 是否可见（当组件不可见时，不监听滚动）
 * @returns 是否向下滚动（隐藏状态）
 */
export function useScrollHide(isVisible: boolean) {
  const [isScrolledDown, setIsScrolledDown] = useState(false)
  const lastScrollYRef = useRef(0)
  const scrollUpDistanceRef = useRef(0)

  useEffect(() => {
    if (!isVisible) {
      return
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const scrollDelta = currentScrollY - lastScrollYRef.current

      if (scrollDelta > 0) {
        // 向下滚动
        scrollUpDistanceRef.current = 0
        if (currentScrollY > 56) {
          setIsScrolledDown(true)
        }
      } else if (scrollDelta < 0) {
        // 向上滚动，累计距离
        scrollUpDistanceRef.current += Math.abs(scrollDelta)
        // 只有连续向上滚动超过 30px 才显示，避免布局调整导致的微小抖动
        if (scrollUpDistanceRef.current > 30) {
          setIsScrolledDown(false)
        }
      }

      if (currentScrollY <= 0) {
        setIsScrolledDown(false)
        scrollUpDistanceRef.current = 0
      }

      lastScrollYRef.current = currentScrollY
    }

    lastScrollYRef.current = window.scrollY

    let ticking = false
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener("scroll", throttledHandleScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll)
    }
  }, [isVisible])

  return isScrolledDown
}
